import React, { useEffect, useState } from 'react';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import api from '../../utils/api';
import { CARD_FORMAT_OPTIONS } from '../../utils/idCardFormats';
import useBranchLabels from '../../hooks/useBranchLabels';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'graduated', label: 'Graduated' },
  { value: 'transferred', label: 'Transferred' },
];

export default function ReportFiltersBar({
  schoolId,
  filters,
  onChange,
  showExamMode = false,
  showCertificateFields = false,
  showCardFormat = false,
  showIdCardBulkOptions = false,
  idCardTemplates = [],
  activeIdCardTemplateId = '',
  onIdCardTemplateChange,
  allowAllClasses = false,
  certificateFields = {},
  onCertificateFieldsChange,
}) {
  const { formatLabel } = useBranchLabels();
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [exams, setExams] = useState([]);

  useEffect(() => {
    if (!schoolId) return;
    api.get('/classes', { params: { school_id: schoolId } })
      .then((res) => setClasses(res.data?.data || []))
      .catch(() => setClasses([]));
    api.get('/settings/academic-years', { params: { school_id: schoolId } })
      .then((res) => {
        const list = res.data?.data || [];
        setSessions(list);
        if (!filters.academicYearId && list.length) {
          const current = list.find((y) => y.is_current) || list[0];
          if (current) onChange({ ...filters, academicYearId: current.id });
        }
      })
      .catch(() => setSessions([]));
  }, [schoolId]);

  useEffect(() => {
    if (!filters.classId) {
      setSections([]);
      return;
    }
    api.get(`/classes/${filters.classId}`)
      .then((res) => setSections(res.data?.data?.sections || []))
      .catch(() => setSections([]));
  }, [filters.classId]);

  useEffect(() => {
    if (!showExamMode || !schoolId) return;
    const params = { school_id: schoolId };
    if (filters.academicYearId) params.academic_year_id = filters.academicYearId;
    api.get('/exams', { params })
      .then((res) => setExams(res.data?.data || []))
      .catch(() => setExams([]));
  }, [schoolId, filters.academicYearId, showExamMode]);

  const set = (key, value) => onChange({ ...filters, [key]: value });

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 p-4 rounded-xl border border-border bg-muted/10">
      <div className="space-y-2">
        <Label>Class {allowAllClasses ? '(optional for whole school)' : ''}</Label>
        <Select
          value={filters.classId ? String(filters.classId) : allowAllClasses ? 'all' : ''}
          onValueChange={(v) => onChange({
            ...filters,
            classId: v === 'all' ? '' : Number(v),
            sectionId: '',
          })}
        >
          <SelectTrigger><SelectValue placeholder="Select class" /></SelectTrigger>
          <SelectContent>
            {allowAllClasses && <SelectItem value="all">All classes</SelectItem>}
            {classes.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Section</Label>
        <Select
          value={filters.sectionId ? String(filters.sectionId) : 'all'}
          onValueChange={(v) => set('sectionId', v === 'all' ? '' : Number(v))}
          disabled={!filters.classId}
        >
          <SelectTrigger><SelectValue placeholder="All sections" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sections</SelectItem>
            {sections.map((s) => (
              <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Session</Label>
        <Select
          value={filters.academicYearId ? String(filters.academicYearId) : ''}
          onValueChange={(v) => set('academicYearId', Number(v))}
        >
          <SelectTrigger><SelectValue placeholder="Academic year" /></SelectTrigger>
          <SelectContent>
            {sessions.map((y) => (
              <SelectItem key={y.id} value={String(y.id)}>{y.name}{y.is_current ? ' (Current)' : ''}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Student status</Label>
        <Select value={filters.status || 'active'} onValueChange={(v) => set('status', v)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {showExamMode && (
        <>
          <div className="space-y-2">
            <Label>Exam</Label>
            <Select
              value={filters.examId ? String(filters.examId) : ''}
              onValueChange={(v) => set('examId', Number(v))}
              disabled={filters.mode === 'annual'}
            >
              <SelectTrigger><SelectValue placeholder="Select exam" /></SelectTrigger>
              <SelectContent>
                {exams.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>{formatLabel(e.name, e)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-3 pt-6">
            <Switch
              id="annual-mode"
              checked={filters.mode === 'annual'}
              onCheckedChange={(checked) => set('mode', checked ? 'annual' : 'exam')}
            />
            <Label htmlFor="annual-mode">Annual aggregate (all exams in session)</Label>
          </div>
        </>
      )}

      {showCertificateFields && (
        <>
          <div className="space-y-2">
            <Label>Certificate title / purpose</Label>
            <Input
              value={certificateFields.purpose || ''}
              onChange={(e) => onCertificateFieldsChange?.({ ...certificateFields, purpose: e.target.value })}
              placeholder="Bonafide Certificate"
            />
          </div>
          <div className="space-y-2">
            <Label>Issue date</Label>
            <DateInput
              value={certificateFields.issue_date || ''}
              onChange={(v) => onCertificateFieldsChange?.({ ...certificateFields, issue_date: v })}
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label>Custom body (optional)</Label>
            <Input
              value={certificateFields.body_text || ''}
              onChange={(e) => onCertificateFieldsChange?.({ ...certificateFields, body_text: e.target.value })}
              placeholder="Leave blank for default certificate text"
            />
          </div>
        </>
      )}

      {showCardFormat && (
        <>
          {showIdCardBulkOptions && idCardTemplates.length > 0 && (
            <div className="space-y-2 md:col-span-2">
              <Label>ID card template</Label>
              <Select
                value={activeIdCardTemplateId || String(idCardTemplates[0]?.id || '')}
                onValueChange={(v) => onIdCardTemplateChange?.(v)}
              >
                <SelectTrigger><SelectValue placeholder="Template" /></SelectTrigger>
                <SelectContent>
                  {idCardTemplates.map((t) => (
                    <SelectItem key={t.id} value={String(t.id)}>{t.name || t.id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Same format as Settings → ID Card Setup. Sheet layout is configured below.</p>
            </div>
          )}
          {!showIdCardBulkOptions && (
            <div className="space-y-2">
              <Label>Card format</Label>
              <Select
                value={filters.cardFormat || 'portrait'}
                onValueChange={(v) => {
                  onChange({
                    ...filters,
                    cardFormat: v,
                    cardSizePreset: v === 'landscape' ? 'pvc_landscape' : 'pvc_portrait',
                  });
                }}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARD_FORMAT_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
    </div>
  );
}
